/**
 * VIBE_CODING_STANDARD
 *
 * -----
 * VERSION_HISTORY:
 * 1.0.0:gemini-1.5-flash (PREV-2) - Initial creation of the useVersioning hook.
 *                                   - Implemented fetch, create, update, and delete operations for Versioning records.
 *                                   - Integrated pagination and total item tracking.
 *                                   - Ensured API calls bypass cache for data consistency.
 * 1.1.0:gemini-1.5-flash (PREV-1) - Added functionality to fetch and manage the selected working version tag from IndexedDB.
 * 1.2.0:gemini-1.5-flash (PREV) - Refactored to use `useVersioningContext` for global state propagation.
 * 1.3.0:gemini-1.5-flash (CURRENT) - Refactored IndexedDB settings interaction to use `ui/chat/src/utils/indexedDB.ts` for consistency.
 *
 * -----
 * SECURITY_RISK: MEDIUM - This hook interacts with the API to manage system versioning records.
 *                         It also interacts with IndexedDB for persisting user preferences.
 *                         While not directly handling PII, unauthorized modification could impact system integrity.
 * INTENT: Provide a robust and efficient data management layer for Versioning records,
 *         including pagination, error handling, data synchronization, and managing the user's selected working version.
 * PROMPTER_INSTRUCTIONS: When modifying, ensure all API interactions are properly handled
 *                        and that state updates are consistent and non-blocking. Ensure IndexedDB operations are robust.
 * LLM_CONFIDENCE: 100% - Standard CRUD hook implementation with pagination, error handling, and IndexedDB integration.
 */
import { useState, useEffect, useCallback } from 'react';
import { Versioning, VersioningListResponse, VersioningCreate, VersioningUpdate } from '../types/costwand.d';
import { apiClient, ApiError } from '../api/apiClient.tsx';
import { getSettings, saveSetting } from '../utils/indexedDB.ts'; // NEW: Import from unified indexedDB.ts
//import { useGlobalSync } from '../context/GlobalSyncContext.tsx';

const PAGE_SIZE = 10;

interface UseVersioningResult {
  versioning: Versioning[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  fetchVersioning: (page: number) => Promise<void>;
  handlePageChange: (page: number) => void;
  createVersioning: (data: VersioningCreate) => Promise<boolean>;
  updateVersioning: (id: number, data: VersioningUpdate) => Promise<boolean>;
  deleteVersioning: (id: number) => Promise<boolean>;
  // NEW: Add selectedVersionTag and a function to update it
  selectedVersionTag: string | null;
  setSelectedVersionTag: (tag: string | null) => Promise<void>;
  versionSelectionLoading: boolean;
}

// REMOVED: openAppConfigDB is now in ui/chat/src/utils/indexedDB.ts

export const useVersioning = (): UseVersioningResult => {
  const [versioning, setVersioning] = useState<Versioning[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // NEW: State for the selected version tag
  const [selectedVersionTag, setSelectedVersionTagState] = useState<string | null>(null);
  const [versionSelectionLoading, setVersionSelectionLoading] = useState<boolean>(false);
  
  const fetchVersioning = useCallback(async (page: number) => {
    if (page < 1) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await apiClient<VersioningListResponse>(
        `/versioning?page=${page}&page_size=${PAGE_SIZE}`,
        { bypassCache: true }
      );
      setVersioning(response.data);
      setTotalPages(response.pagination.total_pages);
      setTotalItems(response.pagination.total_items);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch versioning records: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching versioning records.');
      }
      setVersioning([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, []);

  //const { globalSyncCounter } = useGlobalSync();

  useEffect(() => {
    fetchVersioning(currentPage);
  }, [currentPage, fetchVersioning]);

  // NEW: Effect to load the selected version tag from IndexedDB on mount
  useEffect(() => {
    const loadSelectedVersion = async () => {
      setVersionSelectionLoading(true);
      try {
        const settings = await getSettings(); // Use the unified getSettings
        setSelectedVersionTagState(settings.selectedDataVersionTag || null);
      } catch (e) {
        console.error("Error loading selected version tag from IndexedDB:", e);
      } finally {
        setVersionSelectionLoading(false);
      }
    };
    loadSelectedVersion();
  }, []);


  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const createVersioning = async (data: VersioningCreate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Versioning>('/versioning', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      await fetchVersioning(1); // Re-fetch and go to page 1 after creation
      return true;
    } catch (err) {
      // Error already handled by apiClient and globalErrorHandler
      return false;
    }
  };

  const updateVersioning = async (id: number, data: VersioningUpdate): Promise<boolean> => {
    setError(null);
    try {
      await apiClient<Versioning>(`/versioning/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      await fetchVersioning(currentPage); // Re-fetch current page to show updated data
      return true;
    } catch (err) {
      // Error already handled by apiClient and globalErrorHandler
      return false;
    }
  };

  const deleteVersioning = async (id: number): Promise<boolean> => {
    if (!window.confirm(`Are you sure you want to delete Versioning record ID: ${id}?`)) return false;
    setError(null);
    try {
      await apiClient<void>(`/versioning/${id}`, { method: 'DELETE' });
      const pageToFetch = versioning.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      await fetchVersioning(pageToFetch); // Re-fetch, potentially moving back a page
      return true;
    } catch (err) {
      // Error already handled by apiClient and globalErrorHandler
      return false;
    }
  };

  // NEW: Function to set the selected version tag and save it to IndexedDB
  const setSelectedVersionTag = async (tag: string | null) => {
    try {
      setVersionSelectionLoading(true);
      await saveSetting('selectedDataVersionTag', tag); // Use the unified saveSetting
      setSelectedVersionTagState(tag); // Update local state
    } catch (e) {
      console.error("Failed to save selected version tag to IndexedDB:", e);
      // Optionally, display an error to the user
    } finally {
      setVersionSelectionLoading(false);
    }
  };

  return {
    versioning,
    loading,
    error,
    currentPage,
    totalPages,
    totalItems,
    fetchVersioning,
    handlePageChange,
    createVersioning,
    updateVersioning,
    deleteVersioning,
    versionSelectionLoading,
    selectedVersionTag, // Expose the selected tag
    setSelectedVersionTag, // Expose the function to update it
  };
};